import React, { useState, useRef } from "react"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import { PaperClipIcon, ClockIcon, PlayIcon } from "@heroicons/react/24/outline"
import Config from "aldoo-ra/Config"
import Tooltip from "aldoo-ra/Tooltip"
import moment from "moment"
import { twMerge } from "tailwind-merge"
import { navigate } from "vike/client/router"
import ImagePreview from "aldoo-ra/CMS/preview/image-preview"
import VideoPreview from "aldoo-ra/CMS/preview/video-preview"

const ChatBubble = ({
  message,
  type = "message",
  variant = "default",
  onTicketClick,
}) => {
  const bubbleRef = useRef(null)

  // Add state for preview components
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false)
  const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [previewFileName, setPreviewFileName] = useState("")

  // Check if message only has file attachments with no body text
  const isFileOnlyMessage =
    (!message.body || message.body.trim() === "") &&
    message.fileAttachments &&
    message.fileAttachments.length > 0

  // Format filename to remove timestamp and hash
  const formatDisplayFilename = (filename) => {
    // Match pattern like _1747213684828_a6335cf12b91668b before the extension
    return filename.replace(/_\d+_[a-z0-9]+(\.[^.]+)$/, "$1")
  }

  // Determine file type based on extension
  const getFileType = (filename) => {
    const extension = filename.split(".").pop().toLowerCase()
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"]
    const videoExtensions = ["mp4", "webm", "ogg", "mov", "avi", "mkv", "flv"]

    if (imageExtensions.includes(extension)) return "image"
    if (videoExtensions.includes(extension)) return "video"
    return "other"
  }

  // Bubble styling logic - proper chat bubble behavior
  const getBubbleStyles = () => {
    if (type === "note") {
      return "bg-amber-50 border border-amber-200 text-admin_text max-w-full"
    }

    switch (variant) {
      case "user":
        return "bg-admin_neutral_1 text-admin_text max-w-[80%] w-fit"
      case "support":
        return "bg-admin_primary text-admin_text_inv max-w-[80%] w-fit"
      default:
        return "bg-admin_neutral_1 text-admin_text max-w-[80%] w-fit"
    }
  }

  // Text color override for Typography components inside support bubbles
  const bubbleTextClass = variant === "support" && type !== "note" ? "text-admin_text_inv" : ""

  // Container alignment logic
  const getContainerStyles = () => {
    if (type === "note") {
      return "flex justify-center"
    }
    switch (variant) {
      case "user":
        return "flex justify-start"
      case "support":
        return "flex justify-end"
      default:
        return "flex justify-start"
    }
  }

  const bubbleStyles = getBubbleStyles()
  const containerStyles = getContainerStyles()

  // Format response time functions (unchanged)
  const formatResponseTime = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A"
    return moment.duration(seconds, "seconds").humanize()
  }

  const formatDetailedResponseTime = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A"

    const duration = moment.duration(seconds, "seconds")
    const days = Math.floor(duration.asDays())
    const hours = duration.hours()
    const minutes = duration.minutes()
    const secs = duration.seconds()

    const parts = []
    if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`)
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`)
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "min" : "mins"}`)
    if (secs > 0 || parts.length === 0)
      parts.push(`${secs} ${secs === 1 ? "sec" : "secs"}`)

    return parts.join(", ")
  }

  // Message body parsing for clickable tickets and base64 images
  const renderMessageWithClickableTickets = (body) => {
    if (!body) return ""

    const ticketIdRegex = /#([A-Z0-9]{9,})/g
    const base64ImageRegex = /\[data:image\/[^;]+;base64,[A-Za-z0-9+/=]+\]/g

    const hasTickets = body.match(ticketIdRegex)
    const hasBase64Images = body.match(base64ImageRegex)

    if (!hasTickets && !hasBase64Images) return body

    const parts = []
    let lastIndex = 0
    let imageCounter = 0

    // Combined regex to match both tickets and base64 images
    const combinedRegex = new RegExp(`${ticketIdRegex.source}|${base64ImageRegex.source}`, 'g')
    let match

    while ((match = combinedRegex.exec(body)) !== null) {
      if (match.index > lastIndex) {
        parts.push(body.substring(lastIndex, match.index))
      }

      const matchText = match[0]
      
      if (matchText.startsWith('#')) {
        // Handle ticket ID
        const ticketId = matchText.substring(1)
        parts.push(
          <span
            key={`ticket-${match.index}`}
            className={`${variant === "support" ? "text-white underline" : "text-admin_primary"} hover:underline cursor-pointer`}
            onClick={() => handleTicketClick(ticketId)}
          >
            #{ticketId}
          </span>
        )
      } else if (matchText.startsWith('[data:image/')) {
        // Handle base64 image
        const base64Data = matchText.slice(1, -1) // Remove brackets
        imageCounter++
        parts.push(
          <div key={`image-${match.index}`} className="my-2">
            <img
              src={base64Data}
              alt={`Embedded image ${imageCounter}`}
              className="max-w-full max-h-[400px] rounded-lg border border-admin_bordered cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => {
                setPreviewFile(base64Data)
                setPreviewFileName(`Embedded image ${imageCounter}`)
                setIsImagePreviewOpen(true)
              }}
              onError={(e) => {
                e.target.style.display = 'none'
                console.warn('Failed to load base64 image:', base64Data.substring(0, 50) + '...')
              }}
            />
          </div>
        )
      }

      lastIndex = match.index + matchText.length
    }

    if (lastIndex < body.length) {
      parts.push(body.substring(lastIndex))
    }

    return parts
  }

  // Ticket click handler (unchanged)
  const handleTicketClick = (ticketId) => {
    if (onTicketClick) {
      onTicketClick(ticketId)
    } else {
      navigate(`/admin/customer-support/tickets/${ticketId}`)
    }
  }

  // Enhanced file attachment rendering with thumbnails and previews
  const renderFileAttachments = () => {
    if (!message.fileAttachments || message.fileAttachments.length === 0)
      return null

    return (
      <div className="mt-4 flex flex-wrap gap-3">
        {message.fileAttachments.map((file, index) => {
          // Use local file preview if uploading
          const localFile = message.localFiles?.[index]
          const filename = file.split("/").pop()
          const displayFilename = formatDisplayFilename(filename)

          // Create object URL for local preview or use server URL
          const fileUrl = (message.isUploading && localFile)
            ? URL.createObjectURL(localFile)
            : `${Config.PLATFORM_API_URL}/${Config.PLATFORM_API_VERSION}/customer-support/files/${file}`

          const fileType = getFileType(filename)

          if (fileType === "image") {
            return (
              <div
                key={index}
                className="cursor-pointer group relative"
                onClick={() => {
                  setPreviewFile(fileUrl)
                  setPreviewFileName(displayFilename)
                  setIsImagePreviewOpen(true)
                }}
              >
                <Tooltip
                  content={
                    <div className="p-1">
                      <img
                        src={fileUrl}
                        alt={displayFilename}
                        className="max-w-[200px] max-h-[200px] object-contain"
                      />
                    </div>
                  }
                >
                  <div className="w-[100px] h-[100px] bg-admin_paper/50 rounded overflow-hidden">
                    <img
                      src={fileUrl}
                      alt={displayFilename}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-admin_black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-2">
                    <Typography
                      variant="admin_txt1"
                      className="text-admin_text_inv truncate max-w-[100px] text-center text-sm"
                    >
                      {displayFilename}
                    </Typography>
                  </div>
                </Tooltip>
              </div>
            )
          } else if (fileType === "video") {
            return (
              <div
                key={index}
                className="cursor-pointer group relative shadow-sm"
                onClick={() => {
                  setPreviewFile(fileUrl)
                  setPreviewFileName(displayFilename)
                  setIsVideoPreviewOpen(true)
                }}
              >
                <Tooltip
                  content={
                    <div className="p-1">
                      <div className="max-w-[200px] max-h-[200px] relative">
                        <video
                          src={fileUrl}
                          className="max-w-full max-h-full object-contain"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="rounded-full bg-admin_black/50 p-2">
                            <PlayIcon className="w-6 h-6 text-admin_text_inv" />
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                >
                  <div className="w-[100px] h-[100px] bg-admin_paper/50 rounded overflow-hidden relative">
                    <video
                      src={fileUrl}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="rounded-full bg-admin_black/50 p-2">
                        <PlayIcon className="w-6 h-6 text-admin_text_inv" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-admin_black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-2">
                    <Typography
                      variant="admin_txt1"
                      className="text-admin_text_inv truncate max-w-[100px] text-center text-sm"
                    >
                      {displayFilename}
                    </Typography>
                  </div>
                </Tooltip>
              </div>
            )
          } else {
            return (
              <a
                key={index}
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2 py-1 bg-admin_paper/50 hover:bg-admin_paper/100 rounded-full shadow-sm"
              >
                <PaperClipIcon className="w-4 h-4" />
                <Typography
                  variant="admin_txt1"
                  className="text-admin_text truncate max-w-[150px] my-0"
                >
                  {displayFilename}
                </Typography>
              </a>
            )
          }
        })}
      </div>
    )
  }

  // Render file-only message attachments with larger thumbnails
  const renderFileOnlyAttachments = () => {
    if (!message.fileAttachments || message.fileAttachments.length === 0)
      return null

    return (
      <div className="flex flex-wrap gap-3">
        {message.fileAttachments.map((file, index) => {
          const filename = file.split("/").pop()
          const displayFilename = formatDisplayFilename(filename)
          const fileUrl = `${Config.PLATFORM_API_URL}/${Config.PLATFORM_API_VERSION}/customer-support/files/${file}`
          const fileType = getFileType(filename)

          if (fileType === "image") {
            return (
              <div
                key={index}
                className="cursor-pointer group relative rounded-lg overflow-hidden"
                onClick={() => {
                  setPreviewFile(fileUrl)
                  setPreviewFileName(displayFilename)
                  setIsImagePreviewOpen(true)
                }}
              >
                <div className="w-[200px] h-[200px] bg-admin_paper/50 rounded-lg overflow-hidden">
                  <img
                    src={fileUrl}
                    alt={displayFilename}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-admin_black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-2">
                  <Typography
                    variant="admin_txt1"
                    className="admin_text_inv truncate max-w-[180px] text-center text-sm"
                  >
                    {displayFilename}
                  </Typography>
                </div>
              </div>
            )
          } else if (fileType === "video") {
            return (
              <div
                key={index}
                className="cursor-pointer group relative rounded-lg overflow-hidden"
                onClick={() => {
                  setPreviewFile(fileUrl)
                  setPreviewFileName(displayFilename)
                  setIsVideoPreviewOpen(true)
                }}
              >
                <div className="w-[200px] h-[200px] bg-admin_paper/50 rounded-lg overflow-hidden relative">
                  <video src={fileUrl} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full bg-admin_black/50 p-2">
                      <PlayIcon className="w-8 h-8 admin_text_inv" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 bg-admin_black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-2">
                  <Typography
                    variant="admin_txt1"
                    className="admin_text_inv truncate max-w-[180px] text-center text-sm"
                  >
                    {displayFilename}
                  </Typography>
                </div>
              </div>
            )
          } else {
            return (
              <a
                key={index}
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-2 bg-admin_paper/50 hover:bg-admin_paper/100 rounded-lg"
              >
                <PaperClipIcon className="w-5 h-5" />
                <Typography
                  variant="admin_txt1"
                  className="text-admin_text truncate max-w-[170px] my-0"
                >
                  {displayFilename}
                </Typography>
              </a>
            )
          }
        })}
      </div>
    )
  }

  // Render response time for support messages (unchanged)
  const renderResponseTime = () => {
    if (variant !== "support" || !message.responseTime) return null

    return (
      <div className="absolute -top-7 right-0">
        <Tooltip
          text={`Actual time: ${formatDetailedResponseTime(
            message.responseTime
          )}`}
        >
          <div className="flex items-center gap-1 bg-admin_info_bg rounded-full flex-nowrap px-2 py-1">
            <ClockIcon className="w-3 h-3" />

            <Typography 
              variant="admin_txt2_dim"
              className="whitespace-nowrap"
            >
              Response: {formatResponseTime(message.responseTime)}
            </Typography>
          </div>
        </Tooltip>
      </div>
    )
  }

  // Rendering note type messages (unchanged)
  if (type === "note") {
    return (
      <div className={containerStyles}>
        <div className={twMerge("rounded-lg p-4", bubbleStyles)}>
          <Typography 
            variant="admin_body" 
            className="break-words word-break-break-word whitespace-pre-wrap"
          >
            {message.authorInfo?.name || message.authorInfo?.email || "Unknown"}
            : {renderMessageWithClickableTickets(message.body)}
          </Typography>
          {renderFileAttachments()}
        </div>
      </div>
    )
  }

  // Render file-only messages with larger thumbnails and no bubble
  if (isFileOnlyMessage) {
    return (
      <div className={containerStyles}>
        <div className="relative min-w-0 rounded-lg shadow-md max-w-[80%] w-fit">
          {renderResponseTime()}

          {renderFileOnlyAttachments()}

          <Typography
            variant="admin_txt2_dim"
            className={`absolute -bottom-5 text-admin_text_secondary/80 whitespace-nowrap ${
              variant === "support" ? "right-2" : "left-2"
            }`}
          >
            {message.authorInfo?.name || message.authorInfo?.email || "Unknown"}{" "}
            - {new Date(message.timestamp).toLocaleString()}
          </Typography>
        </div>

        {/* Image Preview Component */}
        {isImagePreviewOpen && (
          <ImagePreview
            isOpen={isImagePreviewOpen}
            imageUrl={previewFile}
            fileName={previewFileName}
            onClose={() => {
              setIsImagePreviewOpen(false)
              setPreviewFile(null)
            }}
          />
        )}

        {/* Video Preview Component */}
        {isVideoPreviewOpen && (
          <VideoPreview
            isOpen={isVideoPreviewOpen}
            videoUrl={previewFile}
            fileName={previewFileName}
            onClose={() => {
              setIsVideoPreviewOpen(false)
              setPreviewFile(null)
            }}
          />
        )}
      </div>
    )
  }

  // Render regular messages with added preview components
  return (
    <>
      <div className={containerStyles}>
        <div
          ref={bubbleRef}
          className={twMerge(
            "relative rounded-lg p-4 break-words",
            bubbleStyles
          )}
        >
          {renderResponseTime()}

          <Typography
            variant="admin_body"
            className={`my-0 break-words word-break-break-word whitespace-pre-wrap ${bubbleTextClass}`}
          >
            {renderMessageWithClickableTickets(message.body)}
          </Typography>

          {renderFileAttachments()}

          {/* Uploading overlay */}
          {message.isUploading && (
            <div className="absolute inset-0 bg-black/70 rounded-lg flex flex-col items-center justify-center gap-3">
              <Typography
                variant="admin_body"
                className="text-white font-medium"
              >
                Uploading...
              </Typography>
              <Spinner variant="minimal" className="w-6 h-6" />
            </div>
          )}

          <Typography
            variant="admin_txt2_dim"
            className={`absolute -bottom-5 text-admin_text_secondary/80 whitespace-nowrap ${
              variant === "support" ? "right-2" : "left-2"
            }`}
          >
            {message.authorInfo?.name || message.authorInfo?.email || "Unknown"}{" "}
            - {new Date(message.timestamp).toLocaleString()}
          </Typography>
        </div>
      </div>

      {/* Image Preview Component */}
      {isImagePreviewOpen && (
        <ImagePreview
          isOpen={isImagePreviewOpen}
          imageUrl={previewFile}
          fileName={previewFileName}
          onClose={() => {
            setIsImagePreviewOpen(false)
            setPreviewFile(null)
          }}
        />
      )}

      {/* Video Preview Component */}
      {isVideoPreviewOpen && (
        <VideoPreview
          isOpen={isVideoPreviewOpen}
          videoUrl={previewFile}
          fileName={previewFileName}
          onClose={() => {
            setIsVideoPreviewOpen(false)
            setPreviewFile(null)
          }}
        />
      )}
    </>
  )
}

export default ChatBubble
